import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, FileText, ClipboardList, BarChart3, Search, Settings as SettingsIcon, Users, Inbox, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";

const tabs = [
  { to: "/admin", label: "Articles", icon: FileText, end: true, testid: "nav-articles" },
  { to: "/admin/worklog", label: "Work Log", icon: ClipboardList, testid: "nav-worklog" },
  { to: "/admin/reels", label: "Reels", icon: Video, testid: "nav-reels" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/admin/research", label: "Research", icon: Search, testid: "nav-research" },
  { to: "/admin/personas", label: "Personas", icon: Users, testid: "nav-personas" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminNav({ linkedinUrl = "https://www.linkedin.com/company/wisewit-solutions/" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate("/", { replace: true });
    logout();
  };

  return (
    <header className="sticky top-0 z-30 bg-ink/85 backdrop-blur-xl border-b border-hairline">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-8 h-8 bg-safety flex items-center justify-center">
            <span className="font-display font-black text-white text-sm">L</span>
          </div>
          <div className="hidden md:block">
            <div className="font-display font-bold tracking-tightest leading-none">
              Lingent Console
            </div>
            <div className="label-mono text-[10px] text-bone/50 leading-none mt-1">
              WiseWit Solutions
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `label-mono px-3 py-2 border flex items-center gap-2 whitespace-nowrap ${
                  isActive
                    ? "bg-bone text-ink border-bone"
                    : "border-hairline text-bone/70 hover:text-bone hover:bg-surface"
                }`
              }
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-linkedin-link"
            className="hidden lg:inline-flex items-center gap-2 label-mono text-bone/70 hover:text-bone px-3 py-2 border border-hairline"
          >
            <ExternalLink className="w-3.5 h-3.5" /> LinkedIn
          </a>
          <div className="hidden xl:block label-mono text-bone/60 px-2" data-testid="user-email-display">
            {user?.email}
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </div>
    </header>
  );
}
